/**
 * Reusable SAP Fiori (Horizon) UI atoms.
 * Used by every page in the ClearPath portal so the look stays consistent.
 */
import React, { useEffect, useRef } from "react";

/* ─────────────────────────── ObjectPageHeader ─────────────────────────── */
/**
 * Fiori "Object Page" header — a white bar that holds the page title and
 * a single action area on the right.
 */
export function ObjectPageHeader({ title, subtitle, actions }) {
  return (
    <header className="bg-white border-b border-fiori-border-light">
      <div className="max-w-screen-2xl mx-auto px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-fiori-text truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-fiori-text-secondary mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/* ─────────────────────────── PageContainer ─────────────────────────── */
export function PageContainer({ children }) {
  return (
    <div className="max-w-screen-2xl mx-auto px-4 py-4 space-y-4">
      {children}
    </div>
  );
}

/* ─────────────────────────── FilterBar ─────────────────────────── */
/**
 * Fiori FilterBar - a flat card with a dense grid of filter inputs.
 */
export function FilterBar({ title = "Filters", onClear, children, footer }) {
  return (
    <section className="fiori-card">
      <div className="fiori-card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-fiori-text-secondary"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L15 13.414V19a1 1 0 01-1.447.894l-4-2A1 1 0 019 17v-3.586L3.293 6.707A1 1 0 013 6V4z"
            />
          </svg>
          <span className="fiori-card-title text-sm">{title}</span>
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="fiori-btn-emphasized text-xs"
          >
            Clear
          </button>
        )}
      </div>
      <div className="fiori-card-body space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {children}
        </div>
        {footer}
      </div>
    </section>
  );
}

/* ─────────────────────────── Field / Select / SearchInput ─────────────────────────── */
export function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="fiori-label">
        {label}
      </label>
      {children}
    </div>
  );
}

export function Select({ id, value, onChange, children }) {
  return (
    <select
      id={id}
      value={value}
      onChange={onChange}
      className="fiori-input appearance-none bg-white pr-8 bg-[length:16px_16px] bg-no-repeat bg-[right_0.5rem_center]"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%23556B82' stroke-width='2'><path stroke-linecap='round' stroke-linejoin='round' d='M6 8l4 4 4-4'/></svg>\")",
      }}
    >
      {children}
    </select>
  );
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <svg
        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fiori-text-disabled pointer-events-none"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z"
        />
      </svg>
      <input
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="fiori-input pl-8"
      />
    </div>
  );
}

/* ─────────────────────────── Buttons ─────────────────────────── */
export function FioriButton({
  variant = "default",
  loading = false,
  icon,
  children,
  className = "",
  ...rest
}) {
  const variantClass =
    variant === "emphasized"
      ? "fiori-btn-emphasized"
      : variant === "ghost"
      ? "fiori-btn-ghost"
      : variant === "negative"
      ? "fiori-btn-negative"
      : "fiori-btn-default";
  return (
    <button {...rest} className={`${variantClass} ${className}`}>
      {loading && (
        <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spinner-linear-spin" />
      )}
      {!loading && icon}
      {children}
    </button>
  );
}

/* ─────────────────────────── Switch (Fiori toggle) ─────────────────────────── */
export function FioriSwitch({ checked, onChange, label, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel || label}
      onClick={() => onChange?.(!checked)}
      className="inline-flex items-center gap-2 select-none"
    >
      {label && (
        <span className="text-sm font-medium text-fiori-text">{label}</span>
      )}
      <span
        className={`relative inline-flex items-center w-[44px] h-[24px] rounded-full transition-colors ${
          checked ? "bg-fiori-brand" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-[18px] w-[18px] rounded-full bg-white shadow transform transition-transform ${
            checked ? "translate-x-[23px]" : "translate-x-[3px]"
          }`}
        />
      </span>
    </button>
  );
}

/* ─────────────────────────── StatusPill ─────────────────────────── */
const STATUS_TONES = {
  // Success
  RESOLVED: "success",
  COMPLETED: "success",
  PAID: "success",
  POSTED: "success",
  APPROVED: "success",
  MATCHED: "success",
  SENT: "success",
  "CREDIT MEMO CREATED": "success",
  // Error
  DECLINED: "error",
  REJECTED: "error",
  OVERDUE: "error",
  CANCELLED: "error",
  "INVOICE CANCELLED": "error",
  ERROR: "error",
  // Warning
  OPEN: "warning",
  AWAITING_RESPONSE: "warning",
  "AWAITING RESPONSE": "warning",
  DISPUTE: "warning",
  DISPUTED: "warning",
  "UNDER REVIEW": "warning",
  "CORRECTION IN PROGRESS": "warning",
  "DEBIT MEMO CREATED": "warning",
  // Info
  INITIATED: "info",
  IN_PROGRESS: "info",
  "IN PROGRESS": "info",
  PROCESSING: "info",
  EMAIL_SENT: "info",
  "EMAIL SENT": "info",
};

export function StatusPill({ status }) {
  if (!status) return <span className="text-fiori-text-disabled">-</span>;
  const display = String(status).toUpperCase().replace(/_/g, " ");
  const tone = STATUS_TONES[display] || "neutral";
  const textCls =
    tone === "success"
      ? "text-fiori-success"
      : tone === "error"
      ? "text-fiori-error"
      : tone === "warning"
      ? "text-fiori-warning"
      : tone === "info"
      ? "text-fiori-info"
      : "text-fiori-shell-2";
  return (
    <span className={`inline-flex items-center text-xs font-semibold ${textCls}`}>
      {display}
    </span>
  );
}

/* ─────────────────────────── Spinner ─────────────────────────── */
export function Spinner({ size = "md", className = "" }) {
  const sizeMap = { sm: "w-4 h-4", md: "w-6 h-6", lg: "w-10 h-10" };
  return (
    <span
      className={`inline-block rounded-full border-2 border-fiori-brand border-t-transparent animate-spinner-linear-spin ${sizeMap[size]} ${className}`}
    />
  );
}

/* ─────────────────────────── EmptyState ─────────────────────────── */
export function EmptyState({ title = "No data", description, icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      {icon || (
        <svg
          className="w-10 h-10 text-fiori-text-disabled mb-2"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h7m4-5l3 3m0 0l-3 3m3-3H10"
          />
        </svg>
      )}
      <p className="text-sm font-medium text-fiori-text">{title}</p>
      {description && (
        <p className="text-xs text-fiori-text-secondary mt-1">{description}</p>
      )}
    </div>
  );
}

/* ─────────────────────────── Dialog ─────────────────────────── */
export function Dialog({
  show,
  title,
  onClose,
  children,
  footer,
  size = "md",
}) {
  // Lock body scroll when open
  useEffect(() => {
    if (!show) return;
    const prevOverflow = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPad;
    };
  }, [show]);

  if (!show) return null;

  const sizeClass =
    size === "sm"
      ? "max-w-sm"
      : size === "lg"
      ? "max-w-4xl"
      : size === "xl"
      ? "max-w-6xl"
      : "max-w-md";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fiori-shell/40 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-fiori-lg shadow-fiori-popover w-full ${sizeClass} flex flex-col max-h-[85vh] overflow-hidden`}
      >
        {(title || onClose) && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-fiori-border-light">
            <h2 className="text-base font-semibold text-fiori-text">{title}</h2>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-fiori-text-secondary hover:text-fiori-text text-xl leading-none w-7 h-7 inline-flex items-center justify-center rounded hover:bg-fiori-card-hover focus:outline-none"
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 px-4 py-3 border-t border-fiori-border-light bg-fiori-page-2/40">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── Toolbar ─────────────────────────── */
export function Toolbar({ left, right }) {
  return (
    <div className="bg-white border border-fiori-border-light rounded-fiori px-3 py-2 flex flex-wrap gap-2 items-center justify-between">
      <div className="flex flex-wrap gap-2 items-center">{left}</div>
      <div className="flex flex-wrap gap-2 items-center">{right}</div>
    </div>
  );
}

/* ─────────────────────────── ResponsiveTable ─────────────────────────── */
/**
 * Thin wrapper that gives tables the Fiori "Responsive Table" look.
 * Children should be a regular <thead>/<tbody>.
 */
export function ResponsiveTable({ children }) {
  return (
    <div className="fiori-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="fiori-table">{children}</table>
      </div>
    </div>
  );
}

/* ─────────────────────────── ActionsCell (mini list of action steps) ─────────────────────────── */
export function ActionsCell({ items }) {
  const [hovered, setHovered] = React.useState(false);

  if (!Array.isArray(items) || items.length === 0)
    return <span className="block text-center text-gray-400 text-xs italic">—</span>;

  // Latest Complete action, fall back to last item
  const summary    = [...items].reverse().find((d) => (d.status || "").toLowerCase() === "complete") || items[items.length - 1];
  const summaryText= summary?.description || summary?.agent_action || "Action recorded";
  const isComplete = (summary?.status || "").toLowerCase() === "complete";
  const isPending  = (summary?.status || "").toLowerCase() === "pending";

  const dotColor = isComplete ? "bg-green-500" : isPending ? "bg-amber-400" : "bg-gray-400";
  const textColor= isComplete ? "text-gray-800"  : isPending ? "text-amber-800"  : "text-gray-600";

  return (
    <div
      className="relative inline-flex flex-col items-center max-w-full"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Single-line summary — dot + truncated text, no background card */}
      <div className={`flex items-center justify-center gap-1.5 cursor-default ${textColor}`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
        <span className="text-xs truncate max-w-[220px]">{summaryText}</span>
      </div>

      {/* Hover tooltip */}
      {hovered && items.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-50 w-80 bg-white border border-gray-200 rounded-xl shadow-xl p-3 space-y-2.5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">All Agent Actions</p>
          {items.map((d, i) => {
            const st  = (d.status || "").toLowerCase();
            const dot = st === "complete" ? "bg-green-500" : st === "pending" ? "bg-amber-400" : "bg-gray-400";
            const txt = st === "complete" ? "text-green-700" : st === "pending" ? "text-amber-700" : "text-gray-600";
            return (
              <div key={i} className="flex items-start gap-2">
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${txt}`}>
                    {d.description || d.agent_action || d.action || JSON.stringify(d)}
                  </p>
                  {d.timestamp && (
                    <p className="text-xs text-gray-400 mt-0.5 font-mono">{d.timestamp}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── IconButton ─────────────────────────── */
export function IconButton({ title, onClick, children, tone = "default" }) {
  const toneClass =
    tone === "danger"
      ? "border-fiori-error text-fiori-error hover:bg-fiori-error-bg"
      : "border-fiori-border text-fiori-brand hover:bg-fiori-page-2";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center w-10 h-10 rounded-fiori border bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-fiori-brand ${toneClass}`}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── Custom hook: focus trap not needed for these dialogs ─────────────────────────── */
export function useClickOutside(onOutside) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside?.();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onOutside]);
  return ref;
}
